import { useState, useEffect } from 'react'
import { StatusAssinatura } from '../components/StatusAssinatura'
import { useAssinatura } from '../hooks/useAssinatura'
import { useAuth } from '../contexts/AuthContext'
import { createClient } from '@supabase/supabase-js'
import { Clock, Zap, Crown, Star, CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

interface Pagamento {
    id: string
    txid: string
    end_to_end_id: string | null
    valor: number
    status: string
    data_vencimento: string
    data_pagamento: string | null
    metodo_pagamento: string
    tentativa_numero: number
    plano_nome: string
    created_at: string
}

interface RecorrenciaPixRow {
    id: string
    id_rec_efi: string
    valor: number
    status: string
    data_inicio: string
    created_at: string
}

export default function Assinatura() {
    const { assinatura, loading } = useAssinatura()
    const { user } = useAuth()
    const [pagamentos, setPagamentos] = useState<Pagamento[]>([])
    const [loadingPagamentos, setLoadingPagamentos] = useState(false)

    const carregarPagamentos = async () => {
        if (!user) return

        setLoadingPagamentos(true)
        try {
            const { data } = await supabase.rpc('listar_pagamentos_usuario', {
                p_user_id: user.id,
                p_limit: 100,
                p_offset: 0
            })

            let pagamentosReais: Pagamento[] = []
            if (data?.sucesso) {
                pagamentosReais = data.pagamentos || []
            }

            // Fallback: incluir recorrências EFI recentes ainda não refletidas na tabela pagamentos.
            const txidsExistentes = new Set(pagamentosReais.map((p) => p.txid))
            const { data: corretorData } = await supabase
                .from('corretores')
                .select('id')
                .eq('user_id', user.id)
                .maybeSingle()

            let pagamentosFallback: Pagamento[] = []
            if (corretorData?.id) {
                const { data: recorrencias } = await supabase
                    .from('recorrencias_pix')
                    .select('id, id_rec_efi, valor, status, data_inicio, created_at')
                    .eq('user_id', corretorData.id)
                    .order('created_at', { ascending: false })
                    .limit(50)

                pagamentosFallback = (recorrencias || [])
                    .filter((rec: RecorrenciaPixRow) => !txidsExistentes.has(rec.id_rec_efi))
                    .map((rec: RecorrenciaPixRow) => ({
                        id: `rec-${rec.id}`,
                        txid: rec.id_rec_efi,
                        end_to_end_id: null,
                        valor: Number(rec.valor || 0),
                        status: ['aprovada', 'ativa'].includes((rec.status || '').toLowerCase()) ? 'pago' : 'pendente',
                        data_vencimento: rec.data_inicio || rec.created_at,
                        data_pagamento: ['aprovada', 'ativa'].includes((rec.status || '').toLowerCase()) ? rec.created_at : null,
                        metodo_pagamento: 'pix',
                        tentativa_numero: 1,
                        plano_nome: assinatura?.plano?.nome_display || 'Assinatura PIX',
                        created_at: rec.created_at
                    }))
            }

            // Fallback cartão EFI (link / checkout) ainda não refletido em pagamentos
            const { data: cartoes } = await supabase
                .from('assinaturas_efi_cartao')
                .select('id, subscription_id_efi, charge_id_efi, status, valor, created_at, updated_at')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(50)

            const statusCartaoPago = new Set([
                'paid',
                'approved',
                'settled',
                'identified',
                'active',
                'pago',
            ])

            const pagamentosCartao: Pagamento[] = (cartoes || [])
                .map((c) => {
                    const txid = c.charge_id_efi
                        ? `efi-card-${c.charge_id_efi}`
                        : `efi-sub-${c.subscription_id_efi || c.id}`
                    return { row: c, txid }
                })
                .filter(({ txid }) => !txidsExistentes.has(txid))
                .map(({ row: c, txid }) => {
                    const st = String(c.status || '').toLowerCase()
                    const pago = statusCartaoPago.has(st)
                    return {
                        id: `card-${c.id}`,
                        txid,
                        end_to_end_id: null,
                        valor: Number(c.valor || assinatura?.valor_mensal || 0),
                        status: pago ? 'pago' : 'pendente',
                        data_vencimento: c.created_at,
                        data_pagamento: pago ? c.updated_at || c.created_at : null,
                        metodo_pagamento: 'cartao',
                        tentativa_numero: 1,
                        plano_nome: assinatura?.plano?.nome_display || 'Assinatura Cartão',
                        created_at: c.created_at,
                    }
                })

            const merged = [...pagamentosReais, ...pagamentosFallback, ...pagamentosCartao].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )
            setPagamentos(merged)
        } catch (error) {
            console.error('Erro ao carregar pagamentos:', error)
        } finally {
            setLoadingPagamentos(false)
        }
    }

    useEffect(() => {
        if (user) {
            carregarPagamentos()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, assinatura?.id])

    if (loading) {
        return (
            <div className="p-4 lg:p-6 dark:bg-gray-900 min-h-screen">
                <div className="space-y-6">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6"></div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const getPlanoIcon = (nome: string) => {
        switch (nome) {
            case 'start':
                return <Star className="w-6 h-6 text-blue-600" />
            case 'medium':
                return <Zap className="w-6 h-6 text-purple-600" />
            case 'pro':
                return <Crown className="w-6 h-6 text-yellow-600" />
            default:
                return <Star className="w-6 h-6 text-gray-600" />
        }
    }

    const getStatusBadge = (status: string) => {
        const badges = {
            pago: { color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400', icon: CheckCircle, label: 'Pago' },
            pendente: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400', icon: Clock, label: 'Pendente' },
            agendado: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400', icon: Clock, label: 'Agendado' },
            expirado: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', icon: XCircle, label: 'Expirado' },
            cancelado: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400', icon: XCircle, label: 'Cancelado' },
            falha: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', icon: AlertTriangle, label: 'Falha' }
        }
        const badge = badges[status as keyof typeof badges] || badges.pendente
        const Icon = badge.icon
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="w-3 h-3" />
                {badge.label}
            </span>
        )
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    const formatDate = (date: string | null) => {
        if (!date) return '-'
        const dateOnly = date.match(/^(\d{4}-\d{2}-\d{2})/)
        if (!dateOnly) return '-'
        const [year, month, day] = dateOnly[1].split('-')
        return `${day}/${month}/${year}`
    }

    return (
        <div className="p-4 lg:p-6 dark:bg-gray-900 min-h-screen">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            Minha Assinatura
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            Gerencie sua assinatura e acompanhe o status do seu plano
                        </p>
                    </div>
                </div>

                {/* Card de Status da Assinatura */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="lg:col-span-2">
                        <StatusAssinatura />
                    </div>
                </div>

                {/* Detalhes do Plano Atual */}
                {assinatura && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Detalhes do Plano
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Informações do Plano */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    {getPlanoIcon(assinatura.plano.nome)}
                                    <div>
                                        <h3 className="font-medium text-gray-900 dark:text-gray-100">
                                            {assinatura.plano.nome_display}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            R$ {assinatura.valor_mensal.toFixed(2)}/mês
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-gray-400">Status:</span>
                                        <span className={`font-medium ${assinatura.status === 'ativa'
                                            ? 'text-green-600 dark:text-green-400'
                                            : assinatura.status === 'trial'
                                                ? 'text-blue-600 dark:text-blue-400'
                                                : 'text-red-600 dark:text-red-400'
                                            }`}>
                                            {assinatura.status === 'ativa' ? 'Ativa' :
                                                assinatura.status === 'trial' ? 'Período de Teste' :
                                                    assinatura.status === 'cancelada' ? 'Cancelada' :
                                                        assinatura.status === 'expirada' ? 'Expirada' : assinatura.status}
                                        </span>
                                    </div>

                                    {assinatura.data_inicio && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Data de Início:</span>
                                            <span className="text-gray-900 dark:text-gray-100">
                                                {new Date(assinatura.data_inicio).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>
                                    )}

                                    {assinatura.data_proximo_pagamento && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Próximo Pagamento:</span>
                                            <span className="text-gray-900 dark:text-gray-100">
                                                {new Date(assinatura.data_proximo_pagamento).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>
                                    )}

                                    {assinatura.data_cancelamento && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Data de Cancelamento:</span>
                                            <span className="text-gray-900 dark:text-gray-100">
                                                {new Date(assinatura.data_cancelamento).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Recursos do Plano */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                    Recursos Incluídos
                                </h4>

                                <div className="space-y-2">
                                    {assinatura.plano.nome === 'start' && (
                                        <>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                                Até 100 imóveis cadastrados
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                                Relatórios básicos
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                                Suporte por email
                                            </div>
                                        </>
                                    )}

                                    {assinatura.plano.nome === 'medium' && (
                                        <>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                                                Até 500 imóveis cadastrados
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                                                Relatórios avançados
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                                                Suporte prioritário
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                                                Integração com CRM
                                            </div>
                                        </>
                                    )}

                                    {assinatura.plano.nome === 'pro' && (
                                        <>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                                                Imóveis ilimitados
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                                                Relatórios personalizados
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                                                Suporte 24/7
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                                                API personalizada
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                                                Treinamento personalizado
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Histórico de Pagamentos */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Histórico de Pagamentos
                        </h2>
                        <button
                            onClick={carregarPagamentos}
                            disabled={loadingPagamentos}
                            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm flex items-center gap-1 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${loadingPagamentos ? 'animate-spin' : ''}`} />
                            Atualizar
                        </button>
                    </div>

                    {loadingPagamentos ? (
                        <div className="text-center py-8">
                            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
                        </div>
                    ) : pagamentos.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            <p>Nenhum pagamento registrado ainda.</p>
                            <p className="text-sm mt-2">Os pagamentos aparecerão aqui após serem processados.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Mês/Período
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Valor
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Vencimento
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Pagamento
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Método
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {pagamentos.map((pagamento) => (
                                        <tr key={pagamento.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {pagamento.plano_nome || 'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {formatDate(pagamento.created_at)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {formatCurrency(pagamento.valor)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {getStatusBadge(pagamento.status)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                {formatDate(pagamento.data_vencimento)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                {pagamento.data_pagamento ? formatDate(pagamento.data_pagamento) : '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                                                    {pagamento.metodo_pagamento || 'PIX'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {pagamentos.length > 0 && (
                        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                            Total de {pagamentos.length} pagamento(s) registrado(s)
                        </div>
                    )}
                </div>

                {/* Informações Adicionais */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
                    <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                            <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                                Precisa de ajuda?
                            </h3>
                            <p className="text-blue-700 dark:text-blue-300 text-sm mb-3">
                                Nossa equipe de suporte está sempre disponível para ajudar você com sua assinatura.
                            </p>
                            <div className="flex gap-2">
                                <button className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                    Contatar Suporte
                                </button>
                                <button className="inline-flex items-center gap-2 border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                    Central de Ajuda
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
